import React, { useState, useEffect } from "react";
import EAIconButton from "../Buttons/EAIconButton";
import "./EAQuantitySelector.scss";
import { useTranslation } from "react-i18next";

interface IProps {
  value: number;
  onChange: (newValue: number) => void;
  onIncrement?: () => void; // Opcjonalne dla custom logiki increment
  onDecrement?: () => void; // Opcjonalne dla custom logiki decrement
  min?: number;
  max?: number;
  disabled?: boolean; // false = input edytowalny, true = tylko tekst
  incrementDisabled?: boolean;
  decrementDisabled?: boolean;
  className?: string;
  onIncrementDisabledClick?: () => void;
  onDecrementDisabledClick?: () => void;
  showMaxInfo?: boolean; // Nowy prop do pokazywania info o maksimum
  compact?: boolean;
}

const EAQuantitySelector: React.FC<IProps> = ({
  value,
  onChange,
  onIncrement,
  onDecrement,
  min = 0,
  max = 99,
  disabled = false,
  incrementDisabled = false,
  decrementDisabled = false,
  className = "",
  onIncrementDisabledClick,
  onDecrementDisabledClick,
  showMaxInfo = false,
  compact = false,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(value.toString());
  const [isInvalid, setIsInvalid] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Synchronizuj inputValue z value quando value się zmienia z zewnątrz
  useEffect(() => {
    setInputValue(value.toString());
    setIsInvalid(false);
  }, [value]);

  const handleIncrement = () => {
    if (onIncrement) {
      onIncrement();
    } else {
      const newValue = Math.min(value + 1, max);
      onChange(newValue);
    }
  };

  const handleDecrement = () => {
    if (onDecrement) {
      onDecrement();
    } else {
      const newValue = Math.max(value - 1, min);
      onChange(newValue);
    }
  };

  const validateAndParseInput = (inputVal: string): { isValid: boolean; parsedValue: number } => {
    // Sprawdź czy to liczba
    const numValue = parseInt(inputVal);

    if (isNaN(numValue) || inputVal.trim() === "") {
      return { isValid: false, parsedValue: min };
    }

    // Sprawdź zakres i automatycznie ograniczaj do min/max
    const clampedValue = Math.max(min, Math.min(max, numValue));
    const isValid = numValue >= min && numValue <= max;

    return { isValid, parsedValue: clampedValue };
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputVal = e.target.value;
    setInputValue(inputVal);

    // Pokazuj walidację tylko gdy komponent nie jest disabled
    if (!disabled) {
      const { isValid } = validateAndParseInput(inputVal);
      setIsInvalid(!isValid);
    }
  };

  const handleInputBlur = () => {
    const { parsedValue } = validateAndParseInput(inputValue);
    onChange(parsedValue);
    setInputValue(parsedValue.toString());
    setIsInvalid(false);
    setIsEditing(false); // Zakończ edycję po blur
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleInputBlur();
    }

    // Zablokuj niektóre znaki (opcjonalnie)
    if (e.key === "e" || e.key === "E" || e.key === "+" || e.key === "-" || e.key === ".") {
      e.preventDefault();
    }
  };

  const handleValueClick = () => {
    if (!disabled) {
      setIsEditing(true);
    }
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Zaznacz całą wartość gdy input otrzyma focus
    e.target.select();
  };

  const isDecrementDisabled = decrementDisabled || value <= min;
  const isIncrementDisabled = incrementDisabled || value >= max;

  return (
    <div className={`quantity-selector ${className}`}>
      <EAIconButton
        title="Odejmij jeden"
        icon={<span className="quantity-selector__glyph">−</span>}
        onClick={handleDecrement}
        disabled={isDecrementDisabled}
        onDisabledClick={onDecrementDisabledClick}
        compact={compact}
      />

      <div className="quantity-selector__value-container">
        {disabled ? (
          <div className="quantity-selector__value">{value}</div>
        ) : isEditing ? (
          <input
            className={`quantity-selector__input ${isInvalid ? "quantity-selector__input--invalid" : ""}`}
            type="number"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            min={min}
            max={max}
            placeholder={min.toString()}
            autoFocus
            onFocus={handleInputFocus}
          />
        ) : (
          <div
            className="quantity-selector__value quantity-selector__value--clickable"
            onClick={handleValueClick}
            title="Kliknij aby edytować"
          >
            {value}
          </div>
        )}

        {showMaxInfo && <div className="quantity-selector__max-info">MAX. {max}</div>}
      </div>

      <EAIconButton
        title={t("dodaj_jeden")}
        icon={<span className="quantity-selector__glyph">+</span>}
        onClick={handleIncrement}
        disabled={isIncrementDisabled}
        onDisabledClick={onIncrementDisabledClick}
        compact={compact}
      />
    </div>
  );
};

export default EAQuantitySelector;
